/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { AfterViewInit, Component, NgZone, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  AbstractFormComponent,
  BuilderNavigationService,
  CommonActions,
  CostCenterFinderDialogConfig,
  costCenterFinderDialogRoute,
  CostCenterFinderListItem,
  FormActionPipelines,
  FormFlowState,
} from '@gh/builder-shared';
import {
  BuilderPermission,
  ScheduleTaskType,
  ScheduleType,
  TaskRelationType,
} from '@gh/builder-shared-data';
import { TimerEvent } from '@gh/common-internal-shared';
import {
  ActionFlow,
  AppRouter,
  DEFAULT_AUTOSAVE_INTERVALS,
  EnumDictionary,
  getAllNewSelectedEntities,
  getRouteInitData,
  getRouteStaticData,
  HelpID,
  isDirtyTypeNew,
  MutablePageState,
  UserContext,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { Permission } from '@gh/core-auth';
import { MessageSeverity } from '@gh/core-messages';
import { TranslationService } from '@gh/core-mls';
import {
  DialogService,
  FormMode,
  noAnswer,
  NotificationService,
  yesAnswer,
  yesNoCancelConfirmation,
  yesNoConfirmation,
} from '@gh/core-ui';
import { GridLayout } from '@gh/core-ui-grid';
import { getLocalValue, openInNewWindow, setLocalValue } from '@gh/core-util';
import { filter$, finalize$, first$, map$, switchMap$ } from '@gh/rx/operators';
import {
  GanttChartComponent,
  GanttTask,
  getGanttTaskId,
  isGanttTaskCanBeOpened,
  isGanttTaskCanHaveSubtasks,
} from '@gh/builder-shared-gantt-chart';
import {
  ScheduleTemplateGanttDetailsBusinessService,
} from '../../resources/schedule-template-gantt-details-business.service';
import {
  newScheduleTemplateCostCenterTask,
  newScheduleTemplateEmptyTask,
  SCHEDULE_TEMPLATE_TASK_START_DATE,
  ScheduleTemplateGanttDetails,
  ScheduleTemplateGanttTask,
} from '../../resources/schedule-template-gantt-details.model';
import { ScheduleTemplateAelEntityType } from '../../resources/scheduling-schedule-template.model';
import { max, omit, values } from 'lodash';
import { Observable } from 'rxjs';

const SCHEDULE_TEMPLATE_GANTT_AUTOSAVE_LOCAL_KEY = 'schedule-template-gantt:autosave';

@Component({
  standalone: false,
  selector: 'gh-schedule-template-gantt-details',
  templateUrl: 'schedule-template-gantt-details.component.html',
})
export class ScheduleTemplateGanttDetailsComponent extends AbstractFormComponent<ScheduleTemplateGanttDetails>
  implements OnInit, AfterViewInit {

  autosaveIntervals = DEFAULT_AUTOSAVE_INTERVALS;
  currentAutosaveInterval = getLocalValue(SCHEDULE_TEMPLATE_GANTT_AUTOSAVE_LOCAL_KEY, '30m');
  autoSaveActivityTick = 0;

  gridLayout: GridLayout;
  taskTypeDict: EnumDictionary<ScheduleTaskType>;
  relationshipTypeDict: EnumDictionary<TaskRelationType>;

  @ViewChild(GanttChartComponent, { static: false }) gantt: GanttChartComponent;
  scheduleTemplate: ScheduleTemplateGanttDetails;
  form: MutablePageState<ScheduleTemplateGanttDetails>;
  reportUrl: string;
  initialRatio: number;
  defaultInitialRatio: number = 0.5;
  nextTaskNumber: number;
  minDate = SCHEDULE_TEMPLATE_TASK_START_DATE;
  isUndoDisabled$: Observable<boolean>;
  isRedoDisabled$: Observable<boolean>;
  private scheduleType: ScheduleType = getRouteStaticData(this.route)['type'];

  constructor(private route: ActivatedRoute,
              private ngZone: NgZone,
              private router: AppRouter,
              private translations: TranslationService,
              private userContext: UserContext,
              private dialogs: DialogService,
              private navigationService: BuilderNavigationService,
              private notifications: NotificationService,
              private commonActions: CommonActions,
              private pipelines: FormActionPipelines,
              private business: ScheduleTemplateGanttDetailsBusinessService) {
    super();
    this.saveBeforeSaveNote = this.saveBeforeSaveNote.bind(this);
  }

  get name(): string {
    return this.form.value.name;
  }

  get tasks(): GanttTask[] {
    return this.form.value.tasks;
  }

  get isConstructionSchedule(): boolean {
    return this.scheduleType === ScheduleType.Construction;
  }

  get documentParentTitle(): string {
    return {
      [ScheduleType.Construction]: 'lbl_construction_sch_template',
      [ScheduleType.Sales]: 'lbl_sales_sch_template',
      [ScheduleType.PreConstruction]: 'lbl_pre_construction_sch_template',
      [ScheduleType.Maintenance]: 'lbl_maintenance_sch_template',
    }[this.scheduleType];
  }

  get helpId(): HelpID {
    return {
      [ScheduleType.Construction]: 'setup/schedules/constructions/gantt-chart',
      [ScheduleType.Sales]: 'setup/schedules/sales/gantt-chart',
      [ScheduleType.PreConstruction]: 'setup/schedules/pre-constructions/gantt-chart',
      [ScheduleType.Maintenance]: 'setup/schedules/warranty/gantt-chart',
    }[this.scheduleType];
  }

  get completeFlow(): ActionFlow<FormFlowState<ScheduleTemplateGanttDetails>> {
    const bounds = this.gantt.api.calculateBounds();
    this.form.value.duration = bounds.duration;
    this.form.value.tasks = <ScheduleTemplateGanttTask[]>this.gantt.api.getAllTasks();
    return this.pipelines.complete({
      validateForm: this.commonActions.validateWithEffect(
        () => this.gantt.api.isInvalid() || this.form.invalid,
        () => this.handleErrors()),
      execute: this.commonActions.execute(() => this.saveData()
                                                    .pipe(map$((response) => {
                                                      if (this.isNewTemplate) {
             this.scheduleTemplate.templateId = response.templateId;
             return {
               isNew: true,
               ...response,
             };
         }
                                                      this.scheduleTemplate.templateId = response.templateId;
                                                      return response;
      })),
      ),
      finish: ({ value }) => {
        if (value.warningMessage && value.warningMessage.trim() !== '') {
          this.notifications.warning(value.warningMessage);
        }
        if (((value as any)['isNew'] || this.isNewTemplate) && value.templateId) {
          this.reloadData(omit(value, 'isNew'), true);
          this.navigateToScheduleTemplateEdit(value.templateId);
          return;
        }
        this.reloadData(value, true);
      },
    });
  }

  get isNewTemplate(): boolean {
    return !this.scheduleTemplate.templateId || this.scheduleTemplate.templateId === 'new';
  }

  ngOnInit(): void {
    const { details, gridLayout, taskTypeDict, relationshipTypeDict, reportUrl, filter } = getRouteInitData(this.route);
    this.initialRatio = filter?.primary || this.defaultInitialRatio;
    this.scheduleTemplate = details;
    this.reportUrl = reportUrl;
    this.taskTypeDict = taskTypeDict;
    this.relationshipTypeDict = relationshipTypeDict;
    this.gridLayout = gridLayout;
    
    if (details.warningMessage && details.warningMessage.trim() !== '') {
      this.notifications.warning(details.warningMessage);
    }
    
    const formMode = this.isNewTemplate ? FormMode.CreateMode :
      this.userContext.hasPermission(this.getEditPermission()) ? FormMode.EditMode : FormMode.ViewMode;
    this.form = new MutablePageState(
      formMode,
      details);
    this.nextTaskNumber = (max(values(details.tasks).map((task) => task.taskNumber)) || 0) + 1;
  }

  ngAfterViewInit(): void {
    this.initGanttHistory();
  }

  private initGanttHistory(): void {
    if (!this.gantt?.api) {
      return;
    }
    this.isUndoDisabled$ = this.gantt.api.isUndoDisabled$;
    this.isRedoDisabled$ = this.gantt.api.isRedoDisabled$;
  }

  saveData(): Observable<ScheduleTemplateGanttDetails> {
    return this.isNewTemplate ? this.business.create(this.form.submitValue)
      : this.business.update(this.form.submitValue);
  }

  onCostCenterAdd(): void {
    this.dialogs.openWithInput(costCenterFinderDialogRoute, <CostCenterFinderDialogConfig>{
      multiselection: true,
      filter: {
        isInJobSchedule: true,
      },
    }).afterClosed().pipe(
      filter$(Boolean),
      map$(getAllNewSelectedEntities))
      .subscribe((costCenters: CostCenterFinderListItem[]) => {
        const startTask = this.gantt.api.getStartTask();
        const startDate = startTask ? startTask.startDate : SCHEDULE_TEMPLATE_TASK_START_DATE;
        const nextTaskNumber = this.nextTaskNumber;
        this.gantt.api.addTasks(costCenters
          .map((cc, i) => newScheduleTemplateCostCenterTask(nextTaskNumber + i, cc, startDate)));
        this.nextTaskNumber += costCenters.length;
      });
  }

  reloadData(template: ScheduleTemplateGanttDetails, clearDirty?: boolean): void {
    this.scheduleTemplate = template;
    this.form.setMode(this.isNewTemplate ? FormMode.CreateMode :
      this.userContext.hasPermission(this.getEditPermission())
        ? FormMode.EditMode
        : FormMode.ViewMode);
    this.form.setValue({
                         ...template,
                         tasks: template.tasks.map((task) => {
                           const target = this.form.value.tasks.find((formTask) => formTask.taskNumber === task.taskNumber);
                           return {
                             ...task,
                             opened: target ? target.opened : false,
                             parentTaskId: target && target.parentTaskId ? target.parentTaskId : task.parentTaskId,
                             taskId: target && target.taskId ? target.taskId : task.taskId,
                             dirtyType: target && !clearDirty ? target.dirtyType : undefined,
                           };
                         }),
                       });
    if (!clearDirty) {
      this.gantt.api.addToHistory();
    } else {
      this.form.resetDirty();
      this.gantt.api.resetDirty();
      this.gantt.api.initHistory();
    }
    setTimeout(() => {
      this.gantt.api.reDrawRows();
      this.gantt.refreshTimeLine();
    }, 50);
  }

  goToAel(): void {
    const params = {
      [ScheduleType.Construction]: {
        entityId: this.form.value.templateId || undefined,
        entityType: ScheduleTemplateAelEntityType.Construction,
        label: 'lbl_construction_sch_template',
      },
      [ScheduleType.Sales]: {
        entityId: this.form.value.templateId || undefined,
        entityType: ScheduleTemplateAelEntityType.Sales,
        label: 'lbl_sales_sch_template',
      },
      [ScheduleType.PreConstruction]: {
        entityId: this.form.value.templateId || undefined,
        entityType: ScheduleTemplateAelEntityType.PreConstruction,
        label: 'lbl_pre_construction_sch_template',
      },
      [ScheduleType.Maintenance]: {
        entityId: this.form.value.templateId || undefined,
        entityType: ScheduleTemplateAelEntityType.Maintenance,
        label: 'lbl_maintenance_sch_template',
      },
    };
    this.navigationService.goToAel(params[this.scheduleType]);
  }

  onOpenReport(): void {
    if (this.form.dirty) {
      this.notifications.error('msg_save_first');
    } else if (this.scheduleTemplate.templateId) {
      openInNewWindow(this.reportUrl);
    }
  }

  onTaskAdd(): void {
    const selectedTask = this.gantt.api.getSelectedTask();
    if (!selectedTask) {
      this.addEmptyTask();
      return;
    }
    const taskIndex = this.gantt.api.getTaskIndex(selectedTask.taskId);
    this.addEmptyTask(selectedTask.parentTaskId, taskIndex + 1);
  }

  onSiblingTaskAdd(sourceSibling: GanttTask): void {
    const taskIndex = this.gantt.api.getTaskIndex(getGanttTaskId(sourceSibling));
    this.addEmptyTask(sourceSibling.parentTaskId, taskIndex + 1);
  }

  onSubTaskAdd(parentTask: GanttTask): void {
    this.addEmptyTask(getGanttTaskId(parentTask));
  }

  onNotSubTask(task: GanttTask): void {
    this.gantt.api.bubbleTask(getGanttTaskId(task));
  }

  onTaskOpen(task: GanttTask): void {
    if (this.form.dirty || this.isNewTemplate) {
      const confirmation$ = this.dialogs.confirmation(yesNoCancelConfirmation('msg_unsaved_info'));
      confirmation$.pipe(
        filter$(yesAnswer),
        switchMap$(() => this.completeFlow.run({
          form: this.form,
          value: this.form.submitValue,
          trigger: 'process',
        }).toFinalState()),
        // We have to wait until next change detection is finished
        // to allow GanttChart to merge state of saved tasks.
        // GanttChart updates physicalTaskIds of all tasks.
        switchMap$(() => this.ngZone.onMicrotaskEmpty.pipe(first$())))
        .subscribe(() => this.openTask(getGanttTaskId(task), task.taskNumber, true));

      confirmation$.pipe(
        filter$(noAnswer))
        .subscribe(() => {
          if (this.isNewTemplate) {
            this.notifications.error('msg_sch_no_task');
            return;
          }
          this.openTask(getGanttTaskId(task));
        });
    } else {
      this.openTask(getGanttTaskId(task));
    }
  }

  onTaskRemove(task: GanttTask): void {
    this.dialogs.confirmation(yesNoConfirmation(task.type === ScheduleTaskType.Summary
      ? 'msg_sch_sum_task_delete'
      : 'msg_sch_template_task_delete')).pipe(
      filter$(yesAnswer))
      .subscribe(() => this.gantt.api.removeTask(getGanttTaskId(task)));
  }

  canBeOpened(task: GanttTask): boolean {
    return isGanttTaskCanBeOpened(task);
  }

  canHaveSubtasks(task: GanttTask): boolean {
    return isGanttTaskCanHaveSubtasks(task);
  }

  onDirtyChange(isDirty: boolean): void {
    this.form.setDirty(isDirty);
    if (isDirty) {
      this.autoSaveActivityTick++;
    }
  }

  onAutoSave(event: TimerEvent): void {
    if (!this.form.dirty) {
      return;
    }
    const savingNotification = this.notifications.show({
      messageId: 'lbl_saving',
      severity: MessageSeverity.Success,
      hideDelay: 30000,
    });
    const flow = this.completeFlow;
    const save$ = flow.run({ trigger: 'process', value: this.form.submitValue, form: this.form }).toFinalState().pipe(
      finalize$(() => {
        savingNotification.close();
      })
    );
    event.waitFor(save$);
  }

  onChangeAutosaveInterval(interval: string): void {
    setLocalValue(SCHEDULE_TEMPLATE_GANTT_AUTOSAVE_LOCAL_KEY, interval);
  }

  onNameChange(name: string): void {
    this.form.value.name = name;
    this.scheduleTemplate.name = name;
    this.form.setDirty(true);
  }

  onSizeChange(size: any): void {
    this.business.saveFilter(size);
  }

  undo(): void {
    this.gantt.api.undo();
  }

  redo(): void {
    this.gantt.api.redo();
  }

  saveBeforeSaveNote(): any {
    return this.completeFlow.run({
                                   form: this.form,
                                   value: this.form.submitValue,
                                   trigger: 'process',
                                 });
  }

  private addEmptyTask(parentTaskId?: ID, branchIndex?: number): void {
    const parentTask = parentTaskId ? this.gantt.api.getTask(parentTaskId) : this.gantt.api.getStartTask();
    const startDate = parentTask ? parentTask.startDate : SCHEDULE_TEMPLATE_TASK_START_DATE;
    const nextTaskNumber = this.nextTaskNumber;
    this.nextTaskNumber += 1;
    const name = `${this.translations.get('lbl_new_task')}`;
    this.gantt.api.addTask(
      newScheduleTemplateEmptyTask(
        nextTaskNumber,
        name,
        startDate,
        parentTaskId),
      branchIndex);
  }

  private getEditPermission(): Permission {
    return {
      [ScheduleType.Construction]: BuilderPermission.Setup.Schedules.ConstructionTemplates.EDIT,
      [ScheduleType.Sales]: BuilderPermission.Setup.Schedules.SalesTemplates.EDIT,
      [ScheduleType.PreConstruction]: BuilderPermission.Setup.Schedules.PreConstructionTemplates.EDIT,
      [ScheduleType.Maintenance]: BuilderPermission.Setup.Schedules.MaintenanceTemplates.EDIT,
    }[this.scheduleType];
  }

  private handleErrors(): void {
    this.notifications.error('msg_he_incorrect_data_in_tables');
    this.gantt.api.navigateToInvalid();
  }

  private openTask(taskId: ID, taskNumber?: number, isNew?: boolean): void {
    const formTask = this.form.value.tasks.find((item) => item.taskNumber === taskNumber);
    const task = isNew && formTask ? formTask : this.gantt.api.getTask(taskId);
    if (isDirtyTypeNew(task.dirtyType)) {
      this.notifications.error('msg_sch_no_task');
      return;
    }

    // reset dirty flag to avoid msg_unsaved_info of leave flow
    // (we always call this method after dirty check has been made)
    this.form.resetDirty();
    this.router.navigateToChild([
      '/setup/scheduling/schedule-templates',
      {
        [ScheduleType.Construction]: 'construction',
        [ScheduleType.Sales]: 'sales',
        [ScheduleType.PreConstruction]: 'pre-construction',
        [ScheduleType.Maintenance]: 'maintenance',
      }[this.scheduleType],
      this.form.value.templateId,
      'tasks',
      task.physicalTaskId]);
  }

  private navigateToScheduleTemplateEdit(templateId: ID): void {
    switch (this.scheduleType) {
      case ScheduleType.Construction:
        this.router.switchTo(['setup', 'scheduling', 'schedule-templates', 'construction', templateId, 'gantt-chart']);
        break;
      case ScheduleType.Sales:
        this.router.switchTo(['setup', 'scheduling', 'schedule-templates', 'sales', templateId, 'gantt-chart']);
        break;
      case ScheduleType.PreConstruction:
        this.router.switchTo(['setup', 'scheduling', 'schedule-templates', 'pre-construction', templateId, 'gantt-chart']);
        break;
      case ScheduleType.Maintenance:
        this.router.switchTo(['setup', 'scheduling', 'schedule-templates', 'maintenance', templateId, 'gantt-chart']);
        break;
      default:
        break;
    }
  }
}
